import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock } from 'lucide-react';
import { LocationInput } from './LocationInput';
import { DateSelector } from './DateSelector';
import { HeroPremiumPointerRow, HeroPremiumPreview } from './HeroPremiumPreview';
import { currentCalendarYear } from '@/lib/calendarYear';
import { trackEvent } from '@/utils/analytics';

interface LocationData {
  name: string;
  latitude: number;
  longitude: number;
}

interface UnifiedHeroSectionProps {
  onLocationChange: (location: LocationData | null) => void;
  onDateChange: (date: { month: number; year: number }) => void;
  selectedDate: { month: number; year: number };
  initialLocation?: LocationData | null;
}

export const UnifiedHeroSection: React.FC<UnifiedHeroSectionProps> = ({
  onLocationChange,
  onDateChange,
  selectedDate,
  initialLocation = null
}) => {
  const [selectedLocation, setSelectedLocation] = useState<LocationData | null>(initialLocation);

  const handleLocationChange = (location: LocationData | null) => {
    setSelectedLocation(location);
    onLocationChange(location);
  };

  const currentYear = currentCalendarYear();

  const trackHeroCta = (cta: 'primary' | 'secondary') => {
    try {
      trackEvent('hero_cta_click', { cta });
    } catch {
      // A broken tag must not block navigation or the planner.
    }
  };

  return (
    <div className="relative overflow-hidden min-h-[520px] sm:min-h-[580px] md:min-h-[640px]">
      {/* Background image */}
      <img
        src="/lovable-uploads/hero-milkyway.jpg"
        alt="Milky Way galactic core rising over a dark landscape"
        className="absolute inset-0 w-full h-full object-cover"
      />
      {/* Layered gradient overlay — heavier at bottom for text readability */}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-slate-900/40 via-transparent to-transparent" />

      {/* Content — bottom-left aligned */}
      <div className="relative z-10 px-6 sm:px-10 lg:px-16 pt-20 sm:pt-28 pb-10 sm:pb-14 flex flex-col items-start text-left h-full justify-end max-w-6xl mx-auto">
        <div className="w-full lg:grid lg:grid-cols-[minmax(0,36rem)_25.5rem] lg:justify-between lg:items-end lg:gap-x-10">
          <div>
            <span className="eyebrow text-gold-light mb-4 inline-block">Astrophotography Planning</span>

            <div className="max-w-2xl mb-10 sm:mb-12">
              <h1 className="text-3xl sm:text-4xl md:text-[3.25rem] font-bold text-white tracking-[-0.04em] leading-[1.1] mb-4 text-balance">
                Plan Your <em className="text-gold-light not-italic">Perfect</em> Night
              </h1>
              <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-lg drop-shadow-sm">
                See exactly when the galactic core is visible, how the moon affects your shoot, and which nights are worth the trip.
              </p>
            </div>

            <div className="max-w-xl w-full space-y-3">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 p-5 rounded-lg [&_label]:text-white/80 [&_.text-muted-foreground]:text-white/75">
                <label className="text-sm font-medium text-white/90 mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-white/70" />
                  Location
                </label>
                <LocationInput
                  onLocationChange={handleLocationChange}
                  initialLocation={initialLocation}
                  variant="dark"
                  ctaLabel="Find my best nights"
                  ctaVariant="inline"
                  onCta={() => trackHeroCta('primary')}
                />
                <Link
                  to="/milky-way-calendar"
                  onClick={() => trackHeroCta('secondary')}
                  className="mt-3 inline-block text-xs text-white/75 hover:text-white transition-colors underline underline-offset-2"
                >
                  Or see the <span suppressHydrationWarning>{currentYear}</span> Milky Way calendar
                </Link>
              </div>

              {selectedLocation && (
                <div className="bg-white/10 backdrop-blur-md border border-white/20 p-5 rounded-lg animate-fade-in [&_label]:text-white/80 [&_.text-muted-foreground]:text-white/75">
                  <label className="text-sm font-medium text-white/90 mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-white/70" />
                    Month & Year
                  </label>
                  <DateSelector
                    selectedDate={selectedDate}
                    onDateChange={onDateChange}
                  />
                </div>
              )}
            </div>
          </div>
          {!selectedLocation && <HeroPremiumPreview />}
        </div>
        <div className="w-full max-w-xl lg:max-w-none pt-3 space-y-3">
          {!selectedLocation && <HeroPremiumPointerRow />}
          <p className="text-xs text-white/65 drop-shadow-sm">
            Free to use — no account required.
          </p>
        </div>
      </div>
    </div>
  );
};
